import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { ImgixImage } from '@air/component-imgix-image';
import { cn } from '@air/utils-tailwind';

import { SimilarToAssetChip } from '~/components/SearchCommand/SmartSearchImageSearchStatus/SimilarToAssetChip';
import { getCloudFrontUrlFromImgixUrl } from '~/constants/cloudfront';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useClip } from '~/swr-hooks/clips/useClip';

export type ClipSearchProps = {
  clipId: string;
};

const CLIP_SEARCH_THUMBNAIL_PX = 24;

export const ClipSearch = memo(({ clipId }: ClipSearchProps) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: clip } = useClip(
    { clipId, workspaceId: currentWorkspace?.id || '' },
    { enabled: !!clipId && !!currentWorkspace?.id },
  );
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (clipId) {
      setImageLoaded(false);
    }
  }, [clipId]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const title = useMemo(() => clip?.title || clip?.importedName || '', [clip?.importedName, clip?.title]);
  const image = useMemo(() => {
    if (!clip?.assets?.image) {
      return null;
    }

    return (
      <ImgixImage
        className={cn(
          'object-cover transition-opacity duration-300 ease-out [image-rendering:smooth]',
          'block size-full rounded-4',
          imageLoaded ? 'opacity-100' : 'opacity-0',
        )}
        alt={title}
        fallbackUrl={getCloudFrontUrlFromImgixUrl(clip.assets.image)}
        src={clip.assets.image}
        width={CLIP_SEARCH_THUMBNAIL_PX}
        height={CLIP_SEARCH_THUMBNAIL_PX}
        onLoad={handleImageLoad}
        imgixParams={{
          fit: 'crop',
          cs: 'origin',
        }}
      />
    );
  }, [clip?.assets?.image, handleImageLoad, imageLoaded, title]);

  return (
    <SimilarToAssetChip data-testid="CLIP_SEARCH" image={image}>
      {title}
    </SimilarToAssetChip>
  );
});

ClipSearch.displayName = 'ClipSearch';
